/**
 * Public event viewer route — `/event/<id>` (Step 15).
 *
 * No AdminGate, no Navbar. App.tsx unwraps this before reaching the
 * admin shell so a spectator with the URL lands directly on the map.
 *
 * `:eventId` is the numeric Event.id from `fleet_events` — see
 * `viewer.html:405-441` for the wireframe. The 6-char join_code path
 * (`/join/<CODE>`) is a separate, driver-facing surface owned by
 * Step 14.5; we don't re-route those here.
 *
 * Responsive (2026-06-09): works on mobile + desktop. The old <1024 px
 * "open on desktop" guard is removed; on narrow screens the per-device
 * detail opens as a full-screen overlay instead of the desktop side panel
 * (see BrowseStateView's DetailPanel responsive classes).
 */

import React from 'react';
import { AlertCircle } from 'lucide-react';

import BrowseStateView from '../components/BrowseStateView';
import { useEventLiveDevices } from '../hooks/useEventLiveDevices';

interface EventViewerPageProps {
  eventId: string;
}

export const EventViewerPage: React.FC<EventViewerPageProps> = ({ eventId }) => {
  const numericId = Number(eventId);
  const isValidId = Number.isFinite(numericId) && numericId > 0;
  const { event, devices, loading, error, replay } = useEventLiveDevices(
    isValidId ? numericId : null,
  );

  if (!isValidId) {
    return <CenteredMessage title="Invalid event link" body={`"${eventId}" isn't a valid event id.`} />;
  }
  if (error) {
    return <CenteredMessage title="Event unavailable" body={error} icon="error" />;
  }
  if (loading || !event) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-base font-mono text-sm text-fg-secondary">
        loading event…
      </div>
    );
  }
  return <BrowseStateView event={event} devices={devices} replay={replay} />;
};

export default EventViewerPage;

const CenteredMessage: React.FC<{
  title: string;
  body: string;
  icon?: 'error';
}> = ({ title, body, icon }) => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-bg-base px-6 text-center text-fg">
    {icon === 'error' && <AlertCircle className="size-10 text-status-error" />}
    <div className="text-lg font-semibold">{title}</div>
    <p className="max-w-sm text-sm text-fg-secondary">{body}</p>
  </div>
);
